import { APP_DOMAIN } from "../../constants/brand";
import { Grid, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip } from '@mui/material'
import styles from '../../Styles/ComponentStyles/ProfileStyle/ProfileDetails.module.scss'
import { useContext, useEffect, useState } from 'react'
import { UserContext, UserContextPayload } from '../../Context/UserDetailContext'
import { ProfileContext, ProfileContextPayload } from '../../Context/ProfileDetailsContext'
import { CommonResponseModel, CreateClientFacingUrlPayload, SendClientFacingUrlPayload, UserProfileDetailsResponse } from '../../Models/axiosModel'
import { useSnackbar } from '../GlobalSnackbar/GlobalSnackbar'
import GlobalButton from '../../Utils/GlobalUtilsComponents/GlobalButton'
import IconsColor from '../../Utils/IconsColor'
import ArrowConfirmationPopover from '../ArrowConfirmationPopover/ArrowConfirmationPopover'
import warningIcon from "../../assets/Images/Profile/warningIcon.svg"
import regenerateIcon from "../../assets/Images/Profile/regenerateIcon.svg"
import moment from 'moment';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import Astrict from '../Astrict/Astrict'

const KickitLinkPopupWrapper = ({ userProfileId, userEmail, setFlagKickitBtnClicked }: any) => {
    const { showSnackbar } = useSnackbar();

    const { setFlagLoader, userDetail } = useContext(UserContext) as UserContextPayload
    const {
        getClientFacingUrlDataAPI,
        createClientFacingUrlAPI,
        sendClientFacingUrlEmailAPI,
        deleteClientFacingUrlAPI,
        extendURLExpiryAPI
    } = useContext(ProfileContext) as ProfileContextPayload

    const [tabValue, setTabValue] = useState('Link Details');
    const [email, setEmail] = useState<string>(userEmail);
    const [clientFacingUrlData, setClientFacingUrlData] = useState<any>({});
    const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
    const [deleteAnchorEl, setDeleteAnchorEl] = useState<null | HTMLElement>(null);
    const [regenerateAnchorEl, setRegenerateAnchorEl] = useState<null | HTMLElement>(null);

    const handleRegenerateClick = (event: React.MouseEvent<HTMLElement>) => { setRegenerateAnchorEl(event.currentTarget); };
    const handleRegenerateClose = () => { setRegenerateAnchorEl(null); };
    const handleDeleteClick = (event: React.MouseEvent<HTMLElement>) => { setDeleteAnchorEl(event.currentTarget); };
    const handleDeleteClose = () => { setDeleteAnchorEl(null); };

    const handleCopyLink = () => {
        const link = `https://${userDetail?.user_data?.user_company_name?.toLowerCase()}.${APP_DOMAIN}/profile/calculators?token=${clientFacingUrlData?.client_facing_url_token}&profile_id=${userProfileId}`;
        navigator.clipboard.writeText(link);
        setCopyTooltipOpen(true);
        setTimeout(() => setCopyTooltipOpen(false), 600);
    };

    const handleGetClientFacingUrlData = async () => {
        try {
            setFlagLoader(true);
            const response = await getClientFacingUrlDataAPI(userProfileId) as CommonResponseModel<UserProfileDetailsResponse>;
            if (response?.status && response?.data) {
                setClientFacingUrlData(response?.data?.[0]);
            } else {
                await createClientFacingUrl();
            }
        } catch (error: any) {
            console.error("Exception while generating Kickit link:", error?.message || error);
        } finally {
            setFlagLoader(false);
        }
    };

    const createClientFacingUrl = async () => {
        try {
            if (!email) { showSnackbar("Please enter email address", "error"); return; }
            const payload: CreateClientFacingUrlPayload = { user_profile_id: userProfileId, email, send_email_to_client_flag: false };
            setFlagLoader(true);
            const response = await createClientFacingUrlAPI(payload) as CommonResponseModel<UserProfileDetailsResponse>;
            if (response?.status) {
                // setClientFacingUrlData(response?.data?.[0]);
                await handleGetClientFacingUrlData();
                showSnackbar("Kickit link generated successfully", "success");
            } else {
                console.error(response?.error?.message || "Error generating Kickit link");
            }
        } catch (error: any) {
            console.error("Exception while generating Kickit link:", error?.message || error);
        } finally {
            setFlagLoader(false);
        }
    };

    const handleSendClientFacingUrlEmail = async () => {
        if (!email) { showSnackbar("Please enter email address", "error"); return; }
        const payload: SendClientFacingUrlPayload = { user_profile_id: userProfileId, email };
        try {
            setFlagLoader(true);
            const response = await sendClientFacingUrlEmailAPI(payload);
            if (response?.status) {
                await handleGetClientFacingUrlData();
                showSnackbar("Kickit link sent successfully", "success");
                // setFlagKickitBtnClicked(false);
            } else {
                showSnackbar(response?.error?.message || "Error sending Kickit link", "error");
            }
        } catch (error: any) {
            console.error("Exception while generating Kickit link:", error?.message || error);
        } finally {
            setFlagLoader(false);
        }
    };

    const handleConfirmDelete = async () => {
        const payload = { public_token: clientFacingUrlData?.client_facing_url_token, user_profile_id: userProfileId };
        try {
            setFlagLoader(true);
            const response = await deleteClientFacingUrlAPI(payload);
            if (response?.status) {
                await handleGetClientFacingUrlData();
                showSnackbar("Kickit link deleted successfully!", "success");
            } else {
                showSnackbar(response?.error?.message || "Error deleting Kickit link", "error");
            }
        } catch (error: any) {
            console.error("Exception while deleting Kickit link:", error?.message || error);
        } finally {
            setDeleteAnchorEl(null);
            setFlagLoader(false);
        }
    };

    const handleConfirmRegenerate = async () => {
        try {
            if (clientFacingUrlData?.client_facing_url_token_deleted && !email) {
                showSnackbar("Please enter email address", "error"); return;
            }
            const payload: any = { user_profile_id: userProfileId };
            if (clientFacingUrlData?.client_facing_url_token_deleted) {
                payload.email = email;
            } else {
                payload.public_token = clientFacingUrlData?.client_facing_url_token;
                payload.send_email_to_client_flag = false;
            }
            setFlagLoader(true);
            const response = clientFacingUrlData?.client_facing_url_token_deleted
                ? await createClientFacingUrlAPI(payload)
                : await extendURLExpiryAPI(payload);
            if (response?.status) {
                await handleGetClientFacingUrlData();
                showSnackbar("Kickit link regenerated successfully!", "success");
            } else {
                showSnackbar(response?.error?.message || "Error regenerating Kickit link", "error");
            }
        } catch (error: any) {
            console.error("Exception while regenerating Kickit link:", error?.message || error);
        } finally {
            setRegenerateAnchorEl(null);
            setFlagLoader(false);
        }
    };

    useEffect(() => { handleGetClientFacingUrlData() }, []);

    const isTokenDeleted = Boolean(clientFacingUrlData?.client_facing_url_token_deleted);
    const isTokenExpired = Boolean(clientFacingUrlData?.client_facing_url_expire);

    let linkStatusClass = styles.kickitStatusExpiresOn;
    if (isTokenDeleted) {
        linkStatusClass = styles.kickitStatusDeleted;
    } else if (isTokenExpired) {
        linkStatusClass = styles.kickitStatusExpire;
    }

    let linkStatusLabel: string;
    if (isTokenDeleted) {
        linkStatusLabel = `Deleted on ${moment(clientFacingUrlData?.client_facing_url_token_deleted_timestamp).format('MM/DD/YYYY')}`;
    } else {
        const expiryPrefix = isTokenExpired ? 'Expired on' : 'Expires on';
        linkStatusLabel = `${expiryPrefix} ${moment(clientFacingUrlData?.client_facing_url_token_expiry_date).format('MM/DD/YYYY')}`;
    }

    return (
        <>
            {deleteAnchorEl &&
                <ArrowConfirmationPopover
                    anchorEl={deleteAnchorEl}
                    onClose={handleDeleteClose}
                    onConfirm={handleConfirmDelete}
                    confirmationMessage={"Are you sure you want delete this KickIt Link?"}
                    startIcon={warningIcon}
                />}
            {regenerateAnchorEl &&
                <ArrowConfirmationPopover
                    anchorEl={regenerateAnchorEl}
                    onClose={handleRegenerateClose}
                    onConfirm={handleConfirmRegenerate}
                    confirmationMessage={"Are you sure you want regenerate this KickIt Link?"}
                    startIcon={regenerateIcon}
                />}

            {/* Tabs */}
            <Grid container justifyContent="center" className={styles.kickitTabsWrapper}>
                <Grid item>
                    <div className={styles.kickitTabPill}>
                        <button
                            className={`${styles.kickitTabBtn} ${tabValue === 'Link Details' ? styles.kickitTabBtnActive : ''}`}
                            onClick={() => setTabValue('Link Details')}
                        >
                            Link Details
                        </button>
                        <button
                            className={`${styles.kickitTabBtn} ${tabValue === 'Activity Logs' ? styles.kickitTabBtnActive : ''}`}
                            onClick={() => setTabValue('Activity Logs')}
                        >
                            Activity Logs
                        </button>
                    </div>
                </Grid>
            </Grid>

            {/* Link Details Tab */}
            {tabValue === 'Link Details' && (
                <Grid container gap={3} className={styles.kickitContentWrapper}>

                    {/* Link Row */}
                    <Grid item xs={12}>
                        <Grid container justifyContent="space-between" alignItems="center" className={styles.kickitLinkLabelRow}>
                            <Grid item className={styles.kickitLinkLabel}>Link</Grid>
                            <Grid item className={linkStatusClass}>
                                {linkStatusLabel}
                            </Grid>
                        </Grid>

                        <Grid container alignItems="center" className={styles.kickitLinkBox}>
                            <Grid item xs className={styles.kickitLinkText}>
                                {`https://${userDetail?.user_data?.user_company_name?.toLowerCase()}.${APP_DOMAIN}/profile/calculators?token=${clientFacingUrlData?.client_facing_url_token}&profile_id=${userProfileId}`}
                            </Grid>
                            <Grid item className={styles.kickitLinkActions}>
                                <Tooltip placement="top" title="Copied!" open={copyTooltipOpen} disableFocusListener disableHoverListener disableTouchListener>
                                    <ContentCopyOutlinedIcon onClick={handleCopyLink} sx={{ color: "#5a6a85", fontSize: "20px" }} />
                                </Tooltip>
                                {clientFacingUrlData?.client_facing_url_token_deleted || clientFacingUrlData?.client_facing_url_expire ? (
                                    <IconButton className={styles.kickitIconBtn} onClick={handleRegenerateClick}>
                                        <IconsColor iconName="regenerateIcon" color={regenerateAnchorEl ? "#3B78CE" : "#5a6a85"} />
                                    </IconButton>
                                ) : (
                                    <IconButton className={styles.kickitIconBtn} onClick={handleDeleteClick}>
                                        <IconsColor iconName="deleteIcon" color={deleteAnchorEl ? "#FF2A2A" : "#5a6a85"} />
                                    </IconButton>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Email Row */}
                    <Grid item xs={12}>
                        <Grid container gap={1}>
                            <Grid item xs={12} className={styles.kickitEmailLabel}>
                                Email <Astrict />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter Email"
                                    variant="outlined"
                                    size="small"
                                    className={styles.kickitEmailInput}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Send Button */}
                    <Grid item xs={12} display="flex" justifyContent="center">
                        <GlobalButton
                            value="Send Email"
                            className={styles.kickitSendBtn}
                            onClick={handleSendClientFacingUrlEmail}
                            disabled={clientFacingUrlData?.client_facing_url_token_deleted || clientFacingUrlData?.client_facing_url_expire}
                        />
                    </Grid>
                </Grid>
            )}

            {/* Activity Logs Tab */}
            {tabValue === 'Activity Logs' && (
                <Grid container className={styles.kickitContentWrapper}>
                    <TableContainer className={styles.tableContainer}>
                        <Table>
                            <TableHead>
                                <TableRow className={styles.headerRow}>
                                    <TableCell width="200px" className={styles.tableHeader} sx={{ borderRadius: "6px 0px 0px 6px" }}>DATE & TIME</TableCell>
                                    <TableCell className={styles.tableHeader} sx={{ borderRadius: "0px 6px 6px 0px" }}>ACTIVITY</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clientFacingUrlData?.client_facing_url_activity_log?.map((log: any) => (
                                    <TableRow key={`${log.timestamp}-${log.message}`} sx={{ borderBottom: "1px solid #ffffff7a !important" }}>
                                        <TableCell className={styles.dateCell}>{log.timestamp}</TableCell>
                                        <TableCell className={styles.activityCell}>{log.message}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            )}
        </>
    );
};

export default KickitLinkPopupWrapper;